import { fetchAllPages } from "@arcadeai/ui-kit/utils/fetch-all-pages";
import type { ScopedBinding } from "@/app/types";
import { BFF_URL } from "./const";
import {
  type ApiClient,
  createApiClient,
  type RequestQueryParams,
} from "./http-client";
import type { OrgProjectScope } from "./resource-context";

export type { ApiClient } from "./http-client";

type OrganizationScope = Pick<OrgProjectScope, "organizationId">;

function hasProjectScope(
  scope: OrganizationScope | OrgProjectScope
): scope is OrgProjectScope {
  return "projectId" in scope && typeof scope.projectId === "string";
}

// The engine caps every list endpoint's `limit` at 1,000 (maxListLimit in
// apps/engine/internal/api/base_handler.go); larger values are rejected with
// a 400. Fetches that request the largest available page use this value.
export const ENGINE_MAX_PAGE_SIZE = 1000;

export type EnginePage<T> = {
  items: T[];
  limit: number;
  offset: number;
  total_count: number;
};

export async function getAllEnginePages<T>(
  client: ApiClient,
  path: string,
  params: RequestQueryParams = {}
): Promise<EnginePage<T>> {
  const { items, total_count } = await fetchAllPages(async (offset) => {
    const { data } = await client.get<EnginePage<T>>(path, {
      params: { ...params, limit: ENGINE_MAX_PAGE_SIZE, offset },
    });
    return data;
  }, `Engine ${path}`);
  return { items, limit: ENGINE_MAX_PAGE_SIZE, offset: 0, total_count };
}

const ENGINE_BASE_URL = `${BFF_URL}/api/engine`;
const COORDINATOR_BASE_URL = `${BFF_URL}/api/coordinator`;

// Unscoped experience API routes such as `/api/config/*`.
export const experienceApi = createApiClient({ baseURL: BFF_URL });
export const coordinatorRootApi = createApiClient({
  baseURL: COORDINATOR_BASE_URL,
});

const createProjectEngineApi = (scope: OrgProjectScope): ApiClient => {
  const { organizationId, projectId } = scope;
  return createApiClient({
    baseURL: `${ENGINE_BASE_URL}/orgs/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}`,
  });
};

const createProjectCoordinatorApi = (scope: OrgProjectScope): ApiClient => {
  const { organizationId, projectId } = scope;
  return createApiClient({
    baseURL: `${COORDINATOR_BASE_URL}/orgs/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}`,
  });
};

const createOrganizationEngineApi = (scope: OrgProjectScope): ApiClient => {
  // Get both org and project context - we need project_id for permission checks
  // even though we're using tenant-level bindings for the data
  const { organizationId, projectId } = scope;

  return createApiClient({
    baseURL: `${ENGINE_BASE_URL}/orgs/${encodeURIComponent(organizationId)}`,
    defaultParams: { project_id: projectId },
  });
};

const createOrganizationCoordinatorApi = (
  scope: OrganizationScope
): ApiClient => {
  const { organizationId } = scope;
  return createApiClient({
    baseURL: `${COORDINATOR_BASE_URL}/orgs/${encodeURIComponent(organizationId)}`,
  });
};

export function getEngineApi(
  scope: OrgProjectScope,
  binding: ScopedBinding = "project"
): ApiClient {
  return binding === "tenant"
    ? createOrganizationEngineApi(scope)
    : createProjectEngineApi(scope);
}

export function getCoordinatorApi(
  scope: OrganizationScope | OrgProjectScope
): ApiClient {
  return hasProjectScope(scope)
    ? createProjectCoordinatorApi(scope)
    : createOrganizationCoordinatorApi(scope);
}
